import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, FormControlLabel, IconButton, Modal, Switch, TextField, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import apiClient from "../../axiosClient";
import User from "../../interfaces/user";
import modalStyle from "../../styles/modalStyle";
import DropDownValue from "../baseComponents/dropdownvalue";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import Permissions from "../../interfaces/permissions";
import PermissionSet from "../../interfaces/permissionSet";
import EntityPermissions from "../../interfaces/entityPermissions";
import JWTTokenPayload from "../../interfaces/JWTTokenPayload";
import { decodedJWT, getOwnerName, getOwnerType } from "../../utils/commonUtils";

interface HandlePermissionSetModalProps {
    open: boolean;
    onClose: () => void;
    getAPIFunction: () => Promise<void>;
    selectedPermissionSet?: PermissionSet;
    modalMode: string;
};

const horizontalStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center' };

const HandlePermissionSetModal: React.FC<HandlePermissionSetModalProps> = ({ open, onClose, getAPIFunction, selectedPermissionSet, modalMode }) => {
    const isEditMode = selectedPermissionSet && modalMode === "edit";
    const token: false | JWTTokenPayload = decodedJWT();
    const ownerName = getOwnerName(token);
    const ownerType = getOwnerType(token);
    const permissionsList = useSelector((state: RootState) => state.permissions.permissions).filter(val => val.is_active);
    const [entityPermissions, setEntityPermissions] = useState<EntityPermissions[]>([]);
    const [permission, setPermission] = useState<Permissions | undefined>(undefined);
    const [selectedUser, setSelectedUser] = useState<User | undefined>(undefined);
    const [licenseCount, setLicenseCount] = useState<number>(0);
    const [isPermissionSetActive, setIsPermissionSetActive] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);
    const [users, setUsers] = useState<User[]>([]);

    const fetchEntityPermissions = async () => {
        try {
            setLoading(true);
            const response = await apiClient.get(`/api/v1/entity_permissions?entity_name=${ownerName}&entity_type=${ownerType}&is_active=true`);
            if (response.status === 200) {
                setEntityPermissions(response.data);
            }
        } catch (error: any) {
            if (error.response) {
                alert('Error response data:' + error.response.data.message);
            } else if (error.request) {
                alert('No response received:' + error.request);
            } else {
                alert('Error message:' + error.message);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEntityPermissions();
    }, []);

    useEffect(() => {
        if (isEditMode && users.length > 0) {
            const permissionObject = permissionsList.find(val => val.permission_name === selectedPermissionSet.permission_name);
            setPermission(permissionObject);
            setSelectedUser(users.find(val => val.username === selectedPermissionSet.username));
            if (permissionObject?.permission_type === "licensed") {
                setLicenseCount(selectedPermissionSet.license_count || 0);   
            }
            setIsPermissionSetActive(selectedPermissionSet.is_active);
        }
    }, [users]);

    const handleClose = () => {
        setSelectedUser(undefined);
        setPermission(undefined);
        setLicenseCount(0);
        setIsPermissionSetActive(true);
        onClose();
    };

    const fetchAllUsers = async () => {
        try {
            setLoading(true);
            const response = await apiClient.get('/api/v1/user');
            if (response.status === 200) {
                setUsers((response.data.user_responses as User[]).filter((val: User) => val.username !== 'super_admin'));
            }
        } catch (error: any) {
            if (error.response) {
                alert('Error response data:' + error.response.data.message);
            } else if (error.request) {
                alert('No response received:' + error.request);
            } else {
                alert('Error message:' + error.message);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAllUsers();
    }, []);

    const createOrUpdateUserPermission = async () => {
        try {
            if (!selectedUser || !permission || (permission && permission.permission_type === "licensed" && licenseCount === 0)) {
                alert('Please enter all details');
                return;
            }
            let response;
            const body = [{
                owner_type: selectedUser.owner_type,
                owner_name: selectedUser.owner_name,
                username: selectedUser.username,
                permission_name: permission?.permission_name,
                license_count: licenseCount <= 0 ? null : licenseCount,
                is_active: isPermissionSetActive,
            }];
            if (isEditMode) {
                response = await apiClient.put('/api/v1/permission_set', body);
            } else {
                response = await apiClient.post('/api/v1/permission_set', body);
            }
            if (response.status === 200) {
                if (isEditMode) {
                    alert('User Permission updated successfully');
                } else {
                    alert('User Permission created successfully');
                }
                handleClose();
                await getAPIFunction();
            }
        } catch (error: any) {
            if (error.response) {
                alert('Error response data:' + error.response.data.message);
            } else if (error.request) {
                alert('No response received:' + error.request);
            } else {
                alert('Error message:' + error.message);
            }
        }
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <Box sx={modalStyle}>
                {loading ? (
                    <Box sx={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <CircularProgress color="primary" />
                    </Box>
                ) : (
                    <>
                        <Box sx={horizontalStyle}>
                            <Typography id="modal-title" variant="h6">
                                {isEditMode ? 'Edit' : 'Add New'} User Permission
                            </Typography>
                            <IconButton onClick={handleClose}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <Box sx={{ ...horizontalStyle, mt: 2 }}>
                            <DropDownValue
                                items={users}
                                selectedValue={selectedUser}
                                onSelection={setSelectedUser}
                                label="Select User"
                                displayProp="username"
                                keyProp="username"
                                style={{ width: '49%' }}
                                disabled={isEditMode}
                            />
                            <DropDownValue
                                items={permissionsList.filter(val => entityPermissions.find(value => value.permission_name === val.permission_name))}
                                selectedValue={permission}
                                onSelection={setPermission}
                                label="Select Permission"
                                displayProp="permission_name"
                                keyProp="permission_name"
                                style={{ width: '49%' }}
                                disabled={isEditMode}
                            />
                        </Box>
                        {permission?.permission_type === "licensed" && (
                            <TextField
                                value={licenseCount}
                                onChange={e => setLicenseCount(parseInt(e.target.value))}
                                type="number"
                                fullWidth
                                label="Enter license count for user"
                                sx={{ mt: 2 }}
                            />
                        )}
                        {isEditMode && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isPermissionSetActive}
                                        onChange={e => setIsPermissionSetActive(e.target.checked)}
                                        name="Is Permission Set Active"
                                        size="medium"
                                    />
                                }
                                sx={{ mt: 2 }}
                                label="Will this user permission be active?*"
                                labelPlacement="end"
                            />
                        )}
                        <Box mt={2} textAlign="right">
                            <Button variant="contained" color="primary" onClick={createOrUpdateUserPermission}>
                                <Typography>Submit</Typography>
                            </Button>
                        </Box>
                    </>
                )}
            </Box>
        </Modal>
    );
};

export default HandlePermissionSetModal;